<template>
  <div class="registration-form">
    <p
      class="text-sm text-gray-500 mb-2"
      v-if="featureOn?.('USER-888-new-sign-up-view')"
    >
      <span class="font-semibold text-red-500">*</span>
      Indicates a required field.
    </p>

    <FormError
      :error="error"
      class="mb-6 sticky top-8 inset-x-8 z-20"
      v-if="!featureOn?.('USER-888-new-sign-up-view')"
    ></FormError>

    <FormErrorInline
      :error="error"
      class="mb-4"
      v-if="featureOn?.('USER-888-new-sign-up-view')"
    ></FormErrorInline>

    <form @submit.prevent="signup()" v-keyboard-submit>
      <div
        :class="
          featureOn?.('USER-888-new-sign-up-view') ? ['flex', 'gap-4'] : []
        "
      >
        <InputField
          class="flex-grow mb-4"
          label="First Name"
          type="text"
          v-model="firstName"
          :validation="v$.firstName"
          v-autofocus
          data-testid="first_name"
          required
        ></InputField>

        <InputField
          class="flex-grow mb-4"
          label="Last Name"
          name="lastName"
          type="text"
          v-model="lastName"
          :validation="v$.lastName"
          data-testid="last_name"
          required
        ></InputField>
      </div>

      <Select
        v-if="useRoleSelector && !isLocked('occupation')"
        class="mb-4"
        label="Tell Us About Yourself"
        placeholder="Select Your Role"
        modalTitle="Select Your Role"
        v-model="occupation"
        :options="SignUpOccupations"
        :validation="v$.occupation"
        data-testid="occupation"
        required
      ></Select>

      <span
        class="hidden"
        v-if="useRoleSelector && isLocked('occupation')"
        data-testid="occupation"
        >{{ occupation }}
      </span>

      <InputField
        v-if="!isLocked('email')"
        class="mb-4"
        label="Email Address"
        name="email"
        type="email"
        v-model="email"
        :validation="v$.email"
        data-testid="email"
        required
      ></InputField>

      <InputReadOnly
        v-if="isLocked('email')"
        class="mb-4"
        label="Email"
        :value="email"
        required
      ></InputReadOnly>

      <InputField
        class="mb-4"
        label="Mobile Number"
        name="phoneNumber"
        type="text"
        v-model="phoneNumber"
        :validation="v$.phoneNumber"
        data-testid="phone_number"
        required
      ></InputField>

      <InputField
        label="Password"
        name="password"
        type="password"
        v-model="password"
        :validation="v$.password"
        data-testid="password"
        required
      ></InputField>

      <ul
        class="text-sm mt-1 mb-4 list-disc pl-4"
        :class="v$.password.$invalid ? ['text-red-600'] : ['text-gray-500']"
      >
        <li>Must be 8-32 characters long</li>
        <li>Include at least one lowercase letter</li>
        <li>Include at least one uppercase letter</li>
        <li>Include at least one number</li>
      </ul>

      <InputField
        class="mb-4"
        label="Confirm Password"
        name="password"
        type="password"
        v-model="confirmPassword"
        :validation="v$.confirmPassword"
        data-testid="confirm_password"
        required
      ></InputField>

      <InputFileField
        v-if="useProfilePicture"
        class="mb-4"
        label="Profile Picture"
        accept="image/*"
        :corner-hint="profilePictureHint"
        v-model="profilePicture"
        data-testid="profile_picture"
      ></InputFileField>

      <p
        class="mb-4 font-bold text-sm"
        v-if="!featureOn?.('USER-888-new-sign-up-view')"
      >
        We’ll send you a verification code to complete your sign up, please
        check you’ve correctly entered your mobile number and email.
      </p>

      <Alert
        type="warning"
        border="false"
        close="false"
        class="mb-4"
        v-if="featureOn?.('USER-888-new-sign-up-view')"
      >
        We’ll send you a verification code to complete your sign up, please
        check you’ve correctly entered your mobile number and email.
      </Alert>

      <div class="text-right">
        <snap-button
          button-type="submit"
          :disabled="state$.is('SUBMITTING')"
          shape="circular"
          size="lg"
          variant="primary"
          data-testid="sign_up"
          >Create Account
        </snap-button>
      </div>
    </form>
  </div>
</template>

<script lang="ts">
import { watch } from "vue";
import { Options } from "vue-class-component";
import { mapGetters } from "vuex";

import { Form, Model, Submit } from "@/core/Form";

import Alert from "@/components/snap-ui/Alert.vue";
import FormError from "@/components/ui/FormError.vue";
import FormErrorInline from "@/components/ui/FormErrorInline.vue";
import InputField from "@/components/snap-ui/InputField.vue";
import InputFileField from "@/components/snap-ui/InputFileField.vue";
import InputReadOnly from "@/components/ui/InputReadOnly.vue";
import Select from "@/components/snap-ui/Select.vue";

import { userSignup } from "@/services/user.service";
import * as FullStory from "@/util/fullStory";

import {
  EmailRule,
  EmojiForbiddenRule,
  PasswordRule,
  PhoneNumberRule,
  RequiredRule,
  SameAsRule,
  StringLengthRule,
} from "@/util/validator";

import { getSignUpOccupations } from "@/services/occupation";
import { FeatureToggle } from "@/store/treatments.module";
import { UserOccupation } from "@/types/graphql";

@Options({
  props: {
    prefill: Object,
  },
  components: {
    Alert,
    FormError,
    FormErrorInline,
    InputField,
    InputFileField,
    InputReadOnly,
    Select,
  },
  computed: {
    ...mapGetters(["consumer", "featureOn", "inviteId", "skipConsumerPassing"]),
  },
})
export default class RegistrationForm extends Form {
  prefill: {
    email?: string;
    phoneNumber?: string;
    firstName?: string;
    lastName?: string;
    occupation?: UserOccupation;
  } = {};

  consumer?: string;
  featureOn?: FeatureToggle;
  inviteId?: string;
  skipConsumerPassing?: boolean;

  SignUpOccupations = getSignUpOccupations();

  @Model({
    required: RequiredRule("Please enter a valid email."),
    email: EmailRule("Please enter a valid email."),
  })
  email = "";

  @Model({
    required: RequiredRule(""),
    strong: PasswordRule(""),
  })
  password = "";

  @Model({
    sameAs: SameAsRule("Please make sure your passwords match.", "password"),
  })
  confirmPassword = "";

  @Model({
    required: RequiredRule("Please enter your first name."),
    length: StringLengthRule(
      "First name should be 1 to 50 characters long",
      1,
      50
    ),
    emoji: EmojiForbiddenRule(
      "Emojis and special characters are not supported."
    ),
  })
  firstName = "";

  @Model({
    required: RequiredRule("Please enter your last name."),
    length: StringLengthRule(
      "Last name should be 1 to 50 characters long",
      1,
      50
    ),
    emoji: EmojiForbiddenRule(
      "Emojis and special characters are not supported."
    ),
  })
  lastName = "";

  @Model({
    required: RequiredRule("Please enter a valid mobile number."),
    phone: PhoneNumberRule("Please enter a valid mobile number."),
  })
  phoneNumber = "";
  @Model({}) profilePicture = "";

  @Model({
    required: RequiredRule("Please select an option that best describes you."),
  })
  occupation: UserOccupation | null = null;

  async beforeMount() {
    await this.$store.dispatch("useTreatments");

    watch(
      this.prefill,
      (update) => {
        if (update.email && this.email != update.email)
          this.email = update.email;

        if (update.firstName && this.firstName != update.firstName)
          this.firstName = update.firstName;

        if (update.lastName && this.lastName != update.lastName)
          this.lastName = update.lastName;

        if (update.phoneNumber && this.phoneNumber != update.phoneNumber)
          this.phoneNumber = update.phoneNumber;

        if (update.occupation && this.occupation !== update.occupation)
          this.occupation = update.occupation;
      },
      { immediate: true }
    );

    watch(
      () => this.occupation,
      (occupation) => {
        if (occupation && this.useRoleSelector) {
          FullStory.setPageProperties("Account Creation page", {
            user_type: occupation,
          });
        }
      }
    );
  }

  get useRoleSelector(): boolean {
    return !!this.featureOn?.("USER-523-add-role-selector-to-signup");
  }

  get useProfilePicture(): boolean {
    return !this.featureOn?.("USER-888-new-sign-up-view");
  }

  get profilePictureHint(): string {
    return this.featureOn?.("USER-546-hide-optional-from-profile-picture")
      ? ""
      : "Optional";
  }

  isLocked(property: "email" | "occupation"): boolean {
    return !!this.prefill[property];
  }

  @Submit()
  async signup(): Promise<void> {
    let controls = [
      "firstName",
      "lastName",
      "email",
      "phoneNumber",
      "password",
      "confirmPassword",
    ];

    if (this.useRoleSelector) {
      controls = [...controls, "occupation"];
    }

    if (await this.isPartialValid(controls)) {
      const { availableTransport, result } = await userSignup({
        firstName: this.firstName,
        lastName: this.lastName,
        email: this.email,
        phoneNumber: this.phoneNumber,
        password: this.password,
        inviteId: this.inviteId,

        ...(!this.skipConsumerPassing ? { consumer: this.consumer } : {}),
        ...(!this.skipConsumerPassing && this.consumer
          ? { apps: [this.consumer] }
          : { apps: null }),
        ...(this.useRoleSelector ? { occupation: this.occupation } : {}),
        ...(this.useProfilePicture
          ? { profilePicture: this.profilePicture }
          : {}),
      });

      await this.$store.dispatch("setConfirmationData", {
        availableTransports: availableTransport,
        usedTransports: result,
        email: this.email,

        ...(this.useRoleSelector ? { occupation: this.occupation } : {}),
      });

      FullStory.event("Account Created", {
        user_email: this.email,
        with_avatar: this.profilePicture !== "",
        ...(this.useRoleSelector ? { user_type: this.occupation } : {}),
      });

      await this.$router.push({ name: "verify" });
    }
  }

  reset(): void {
    this.email = "";
    this.phoneNumber = "";
    this.password = "";
    this.firstName = "";
    this.lastName = "";
    this.profilePicture = "";
  }
}
</script>
